/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

// A form's report: one row per filed submission, one column per field. The
// jobs service keeps what each PDF printed (Forms/FormSubmissions.cs); this
// turns it into what the page, a CSV file and an Excel workbook show.

import {
  allFields,
  formatAddress,
  FormDefinition,
  FormField,
  FormValue,
  FormValues,
  hasTyping,
  isAddress,
  isNumericColumn,
  printedType,
  takesValue,
  toNumber
} from './forms.model';

/** One filed form, as the jobs service recorded it. */
export interface FormSubmission {
  reference: string;
  formId: string;
  formTitle: string;
  formVersion: number;
  /** The person who submitted it: their id and their name. */
  by: string;
  byName: string;
  /** ISO time it was printed for filing. */
  at: string;
  signed: boolean;
  /** As printed, by field key; a signature is true. */
  values: FormValues;
  nodeId?: string;
  processId?: string;
}

/** A field's column. A key the form no longer has (from an earlier version) has no field, and is headed by the key. */
export interface ReportColumn {
  key: string;
  label: string;
  field?: FormField;
}

/** Words a cell may need, in the reader's language. */
export interface ReportWords {
  yes: string;
  signed: string;
  rows: (count: number) => string;
}

/**
 * The form's fields that take a value, in the form's order, then any key an
 * earlier version asked for that the form no longer has. Tables are columns
 * too: the page counts their rows, and the workbook gives each a sheet.
 */
export function reportColumns(form: FormDefinition, submissions: FormSubmission[]): ReportColumn[] {
  const fields = allFields(form).filter(takesValue);
  const columns: ReportColumn[] = fields.map((field) => ({ key: field.key, label: field.label || field.key, field }));
  const known = new Set(columns.map((c) => c.key));
  for (const submission of submissions) {
    for (const key of Object.keys(submission.values ?? {})) {
      if (!known.has(key)) {
        known.add(key);
        columns.push({ key, label: key });
      }
    }
  }
  return columns;
}

/** 2026-10-24 as 24/10/2569: the Buddhist Era, as the paper prints it. */
export function thaiDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return match ? `${Number(match[3])}/${Number(match[2])}/${Number(match[1]) + 543}` : (iso ?? '');
}

/** When a submission was made, in Thailand's time: 24/10/2569 14:30. */
export function submittedAt(iso: string): string {
  const at = new Date(iso);
  if (isNaN(at.getTime())) {
    return iso ?? '';
  }
  const local = new Date(at.getTime() + 7 * 3600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${local.getUTCDate()}/${local.getUTCMonth() + 1}/${local.getUTCFullYear() + 543} ${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`;
}

const isNumeric = (column: ReportColumn): boolean => !!column.field && ['number', 'money'].includes(printedType(column.field));

/**
 * A cell as a spreadsheet keeps it: numbers as numbers, so they can be added
 * up; everything else as the text the page shows.
 */
export function cellValue(column: ReportColumn, value: FormValue | undefined, words: ReportWords): string | number {
  if (isNumeric(column)) {
    const number = toNumber(value);
    if (number !== null) {
      return number;
    }
  }
  return cellText(column, value, words);
}

/** A cell as the page shows it. */
export function cellText(column: ReportColumn, value: FormValue | undefined, words: ReportWords): string {
  if (value === null || value === undefined || value === '' || value === false) {
    return '';
  }
  const field = column.field;
  if (field?.type === 'signature') {
    return value ? words.signed : '';
  }
  if (isAddress(value)) {
    return formatAddress(value);
  }
  if (field?.type === 'table' || (Array.isArray(value) && value.length && typeof value[0] === 'object')) {
    const rows = Array.isArray(value) ? (value as Record<string, string | number>[]) : [];
    return words.rows(field ? rows.filter((row) => hasTyping(field, row)).length : rows.length);
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (value === true) {
    return words.yes;
  }
  if (field?.type === 'date') {
    return thaiDate(String(value));
  }
  if (field && isNumeric(column)) {
    const number = toNumber(value);
    if (number !== null) {
      return printedType(field) === 'money'
        ? number.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : number.toLocaleString('th-TH', { maximumFractionDigits: 2 });
    }
  }
  return String(value);
}

/** Whether a submission is one asked for: sent in the date range, and with the words anywhere in its reference, sender or answers. */
export function matches(
  submission: FormSubmission,
  words: string,
  from: string,
  to: string,
  columns: ReportColumn[],
  cellWords: ReportWords
): boolean {
  const day = isoDay(submission.at);
  if ((from && day < from) || (to && day > to)) {
    return false;
  }
  const wanted = words.trim().toLowerCase();
  if (!wanted) {
    return true;
  }
  const texts = [submission.reference, submission.byName, submission.by, ...columns.map((c) => cellText(c, submission.values?.[c.key], cellWords))];
  return texts.some((text) => (text ?? '').toLowerCase().includes(wanted));
}

/** The day a submission was made on, in Thailand, as yyyy-MM-dd (what a date range is compared with). */
export function isoDay(iso: string): string {
  const at = new Date(iso);
  return isNaN(at.getTime()) ? '' : new Date(at.getTime() + 7 * 3600_000).toISOString().slice(0, 10);
}

/** The rows of every submission's table, for a sheet of its own: each row names the submission it came from. */
export function tableSheet(column: ReportColumn, submissions: FormSubmission[]): { headers: string[]; rows: (string | number)[][] } {
  const tableColumns = column.field?.columns ?? [];
  const rows: (string | number)[][] = [];
  for (const submission of submissions) {
    const value = submission.values?.[column.key];
    if (!Array.isArray(value)) {
      continue;
    }
    (value as Record<string, string | number>[])
      .filter((row) => !column.field || hasTyping(column.field, row))
      .forEach((row, i) => {
        rows.push([
          submission.reference,
          i + 1,
          ...tableColumns.map((c) => {
            const cell = row?.[c.key];
            const number = isNumericColumn(c) ? toNumber(cell) : null;
            return number ?? (c.type === 'date' ? thaiDate(String(cell ?? '')) : String(cell ?? ''));
          })
        ]);
      });
  }
  return { headers: tableColumns.map((c) => c.label || c.key), rows };
}

/** RFC 4180 text: every cell quoted when it must be, lines ended CRLF, as Excel expects. */
export function toCsv(headers: (string | number)[], rows: (string | number)[][]): string {
  const cell = (value: string | number): string => {
    const text = String(value ?? '');
    // a leading = + - @ would be run as a formula by a spreadsheet
    const safe = /^[=+\-@\t\r]/.test(text) && typeof value !== 'number' ? `'${text}` : text;
    return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  return [headers, ...rows].map((row) => row.map(cell).join(',')).join('\r\n') + '\r\n';
}

/** "ใบลา คำตอบ 2569-10-24": a file name without characters a file name may not hold. */
export function reportFileName(formTitle: string, today: string): string {
  const title = (formTitle || 'form')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
  return `${title} ${today}`;
}
